import React, { useState, useEffect } from 'react';
import { GetAppQR } from './GetApp';

const C = { bg:'#040000',bg2:'#0e0101',gold:'#f5c842',gold2:'#d4a843',gold3:'#b8922e',red:'#cc0000',red2:'#880000',cream:'#f0e6cc',cream2:'#c8b89a',muted:'#8a7060',border:'rgba(212,168,67,0.15)' };

// Locale -> currency, mirrors Pricing.js
const LOCALE_CURRENCY = {
  'en-IN':'INR','ta-IN':'INR','hi-IN':'INR','te-IN':'INR','ml-IN':'INR',
  'en-US':'USD','en-GB':'GBP','en-AU':'AUD','en-CA':'CAD',
  'en-SG':'SGD','ms-MY':'MYR','en-MY':'MYR','ar-AE':'AED','en-AE':'AED',
  'de-DE':'EUR','fr-FR':'EUR','es-ES':'EUR',
};

// presets are in the currency's MAIN unit; smallestMult converts to paise/cents
const CURRENCY_CONFIG = {
  INR: { symbol:'\u20b9',  flag:'\ud83c\uddee\ud83c\uddf3', presets:[50,100,500,1000], smallestMult:100 },
  USD: { symbol:'$',   flag:'\ud83c\uddfa\ud83c\uddf8', presets:[1,5,10,25],       smallestMult:100 },
  GBP: { symbol:'\u00a3',  flag:'\ud83c\uddec\ud83c\udde7', presets:[1,5,10,25],       smallestMult:100 },
  AUD: { symbol:'A$',  flag:'\ud83c\udde6\ud83c\uddfa', presets:[2,5,15,30],       smallestMult:100 },
  CAD: { symbol:'C$',  flag:'\ud83c\udde8\ud83c\udde6', presets:[2,5,15,30],       smallestMult:100 },
  SGD: { symbol:'S$',  flag:'\ud83c\uddf8\ud83c\uddec', presets:[2,5,15,30],       smallestMult:100 },
  MYR: { symbol:'RM',  flag:'\ud83c\uddf2\ud83c\uddfe', presets:[5,15,50,100],     smallestMult:100 },
  AED: { symbol:'AED', flag:'\ud83c\udde6\ud83c\uddea', presets:[5,15,50,100],     smallestMult:100 },
  EUR: { symbol:'\u20ac',  flag:'\ud83c\uddea\ud83c\uddfa', presets:[1,5,10,25],       smallestMult:100 },
};

function detectCurrency() {
  try {
    const locale = (navigator.language || 'en-IN');
    if (LOCALE_CURRENCY[locale]) return LOCALE_CURRENCY[locale];
    if (locale.indexOf('IN') !== -1) return 'INR';
    const region = locale.split('-')[1];
    const map = { US:'USD', GB:'GBP', AU:'AUD', CA:'CAD', SG:'SGD', MY:'MYR', AE:'AED', DE:'EUR', FR:'EUR', ES:'EUR' };
    return map[region] || 'INR';
  } catch (e) { return 'INR'; }
}

export default function DonateButton({ user }) {
  const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
  const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://nethajiprime-backend.onrender.com';
  const [open, setOpen] = useState(false);
  const [currency, setCurrency] = useState('INR');
  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState(user?.full_name || user?.name || '');
  const [donorEmail, setDonorEmail] = useState(user?.email || '');
  const [donorPhone, setDonorPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (window.Razorpay || !RAZORPAY_KEY) return;
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    const cur = detectCurrency();
    setCurrency(cur);
    setAmount(CURRENCY_CONFIG[cur].presets[1]);
  }, []);

  const cfg = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.INR;
  const finalAmount = customAmount ? parseFloat(customAmount) : amount;

  const changeCurrency = (cur) => {
    setCurrency(cur);
    setCustomAmount('');
    setAmount(CURRENCY_CONFIG[cur].presets[1]);
  };

  const handleDonate = async () => {
    setError(null);
    if (!finalAmount || finalAmount < (currency === 'INR' ? 1 : 0.5)) {
      setError('Please enter a valid amount.');
      return;
    }
    if (!donorName.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!RAZORPAY_KEY) {
      setError('Donations are coming soon!');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = 'Bearer ' + token;

      const smallest = Math.round(finalAmount * cfg.smallestMult);
      const res = await fetch(API_URL + '/donations/create-order', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          amount: smallest,
          currency: currency,
          donor_name: donorName,
          donor_email: donorEmail,
          donor_phone: donorPhone
        }),
      });
      if (!res.ok) throw new Error('Could not start donation. Please try again.');
      const order = await res.json();

      const rzp = new window.Razorpay({
        key: order.key || RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency || currency,
        name: 'Nethaji Prime OTT',
        description: 'Support Nethaji Prime',
        order_id: order.order_id || order.id,
        prefill: { name: donorName, email: donorEmail, contact: donorPhone },
        theme: { color: '#FFD700' },
        handler: async (response) => {
          try {
            const vres = await fetch(API_URL + '/donations/verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(response),
            });
            setLoading(false);
            if (vres.ok) { setSuccess(true); }
            else { setError('Payment verification failed. Please contact support.'); }
          } catch (e) {
            setLoading(false);
            setError('Payment verification failed. Please contact support.');
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.open();
    } catch (e) {
      setLoading(false);
      setError(e.message || 'Something went wrong. Please try again.');
    }
  };

  const reset = () => {
    setOpen(false);
    setSuccess(false);
    setError(null);
    setCustomAmount('');
    setAmount(cfg.presets[1]);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
          background: `linear-gradient(135deg, ${C.gold}, ${C.gold3})`, color: '#1a0505',
          border: 'none', borderRadius: 6, fontFamily: "'Bebas Neue',sans-serif",
          fontSize: 13, letterSpacing: 1, fontWeight: 700, cursor: 'pointer'
        }}>
        <span>♥</span> DONATE
      </button>

      {open && (
        <div onClick={reset} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12,
            maxWidth: 420, width: '100%', padding: 28, position: 'relative'
          }}>
            <button onClick={reset} style={{
              position: 'absolute', top: 16, right: 16, background: 'none', border: 'none',
              color: C.muted, fontSize: 20, cursor: 'pointer'
            }}>✕</button>

            {success ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🙏</div>
                <h2 style={{ color: C.gold, fontFamily: "'Bebas Neue',sans-serif", fontSize: 24, letterSpacing: 1, margin: '0 0 8px' }}>
                  Thank You!
                </h2>
                <p style={{ color: C.cream2, fontSize: 14, lineHeight: 1.6 }}>
                  Your support helps us keep bringing Tamil sociopolitical media to everyone.
                </p>

                <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 18, paddingTop: 16 }}>
                  <div style={{ color: C.gold2, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                    &#128241; Get the Nethaji Prime app
                  </div>
                  <p style={{ color: C.muted, fontSize: 12, margin: '0 0 12px' }}>
                    Download our app to watch on the go.
                  </p>
                  <GetAppQR size={112} />
                </div>
                <button onClick={reset} style={{
                  marginTop: 16, padding: '10px 24px', background: C.gold, color: '#1a0505',
                  border: 'none', borderRadius: 6, fontFamily: "'Bebas Neue',sans-serif",
                  fontSize: 14, letterSpacing: 1, cursor: 'pointer'
                }}>Close</button>
              </div>
            ) : (
              <>
                <h2 style={{ color: C.gold, fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: 1, margin: '0 0 4px' }}>
                  Support Nethaji Prime
                </h2>
                <p style={{ color: C.muted, fontSize: 13, margin: '0 0 16px' }}>
                  Your contribution helps us keep Tamil sociopolitical media free and independent.
                </p>

                {/* Currency switcher */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <span style={{ color: C.muted, fontSize: 12 }}>Currency</span>
                  <select
                    value={currency}
                    onChange={e => changeCurrency(e.target.value)}
                    style={{
                      flex: 1, padding: '8px 10px', background: 'rgba(255,255,255,0.05)',
                      border: `1px solid ${C.border}`, borderRadius: 6, color: C.cream, fontSize: 13
                    }}>
                    {Object.keys(CURRENCY_CONFIG).map(code => (
                      <option key={code} value={code} style={{ color: '#000' }}>
                        {CURRENCY_CONFIG[code].flag} {code} ({CURRENCY_CONFIG[code].symbol})
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
                  {cfg.presets.map(amt => (
                    <button key={amt} onClick={() => { setAmount(amt); setCustomAmount(''); }} style={{
                      padding: '10px 0', borderRadius: 6, cursor: 'pointer',
                      background: (!customAmount && amount === amt) ? C.gold : 'rgba(255,255,255,0.05)',
                      color: (!customAmount && amount === amt) ? '#1a0505' : C.cream,
                      border: `1px solid ${(!customAmount && amount === amt) ? C.gold : C.border}`,
                      fontFamily: "'Bebas Neue',sans-serif", fontSize: 14, letterSpacing: 0.5
                    }}>{cfg.symbol}{amt}</button>
                  ))}
                </div>

                <input
                  type="number"
                  placeholder={`Or enter custom amount (${cfg.symbol})`}
                  value={customAmount}
                  onChange={e => setCustomAmount(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '10px 12px', marginBottom: 12,
                    background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 6,
                    color: C.cream, fontSize: 14
                  }}
                />

                <input
                  type="text"
                  placeholder="Your name"
                  value={donorName}
                  onChange={e => setDonorName(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '10px 12px', marginBottom: 10,
                    background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 6,
                    color: C.cream, fontSize: 14
                  }}
                />

                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={donorEmail}
                  onChange={e => setDonorEmail(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '10px 12px', marginBottom: 10,
                    background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 6,
                    color: C.cream, fontSize: 14
                  }}
                />

                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={donorPhone}
                  onChange={e => setDonorPhone(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '10px 12px', marginBottom: 16,
                    background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 6,
                    color: C.cream, fontSize: 14
                  }}
                />

                {error && (
                  <div style={{ color: '#ff6b6b', fontSize: 13, marginBottom: 12 }}>{error}</div>
                )}

                <button onClick={handleDonate} disabled={loading} style={{
                  width: '100%', padding: '12px 0', background: loading ? C.gold3 : `linear-gradient(135deg, ${C.gold}, ${C.gold3})`,
                  color: '#1a0505', border: 'none', borderRadius: 6, fontFamily: "'Bebas Neue',sans-serif",
                  fontSize: 15, letterSpacing: 1, cursor: loading ? 'default' : 'pointer'
                }}>
                  {loading ? 'Processing...' : `Donate ${cfg.symbol}${finalAmount || 0}`}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
